<template>
  <div data-testid="prompts-details" class="pa-6 d-flex flex-column gap-6">
    <div class="d-flex align-center justify-space-between">
        <Breadcrumbs
        :glCodeText="'Prompts'"
        :glCodeInfo="featureDisplayName"
        :activeCommentary="{}"
        :getStatusLabel="() => {}"
        @back="back"
        />
        <div class="d-flex align-center gap-2">
            <lb-string
            data-testid="prompts__search-input"
            label="Search"
            outlined
            hidedetails
            v-model="searchList"
            ></lb-string>
            <BaseButton
            data-testid="prompts__add-btn"
            color="primary"
            @click="openAddDialog"
            class="white--text"
            label="Add Prompts"
            />
        </div>
    </div>
    
    <v-card class="pa-4 d-flex flex-column gap-4" v-if="currentFeature">
      <div v-for="(prompt, index) in filteredPrompts" :key="index" class="d-flex flex-column gap-3">
        <div class="d-flex align-center justify-space-between">
            <h5 class="">{{ prompt.type }}</h5>
            <div class="d-flex align-center gap-2">
                <StatusIndicator
                  :variant="prompt.status ? 'active' : 'inactive'"
                  :custom-label="prompt.status ? 'Active' : 'Inactive'"
                  class=""
                  size="sm"
                  outlined
                  style="width: auto;"
                  cornerRadius="8px"
                />
                <v-btn 
                    icon 
                    small 
                    @click="editPrompt(prompt)" 
                    data-testid="prompts__edit-btn"
                >
                    <img src="/img/icons/PenEditIcon.svg" alt="Pen Edit Icon" />
                </v-btn>
            </div>
        </div>
        <v-card outlined class="pa-3">
            <pre style="white-space: pre-wrap; font-family: inherit;">{{ prompt.content }}</pre>
        </v-card>
      </div>
      <div v-if="filteredPrompts.length === 0" class="text-center grey--text pa-4">
        No prompts found matching your search.
      </div>
    </v-card>

    <!-- Single Prompt Dialog (add & edit) -->
    <PromptDialog
      v-model="activeDialog"
      :mode="dialogMode"
      :promptData="dialogPromptData"
      :errors="dialogErrors"
      :loading="loading"
      @save="handleDialogSave"
    />
  </div>
</template>

<script>
import Breadcrumbs from '@/modules/varianceanalysis/components/Breadcrumbs.vue';
import BaseButton from '@/views/common/BaseButton.vue';
import StatusIndicator from '@/modules/varianceanalysis/components/StatusIndicator.vue';
import { MESSAGES, PROMPT_MODES } from '@/modules/admin/utils/constants.js';
import PromptDialog from './PromptDialog.vue';

export default {
  name: 'PromptsDetails',
  components: {
    Breadcrumbs,
    BaseButton,
    StatusIndicator,
    PromptDialog
  },
  data() { 
    return {
      loading: false,
      searchList: '',
      // Unified Dialog State
      activeDialog: false,
      dialogMode: PROMPT_MODES.ADD,
      dialogPromptData: {},
      dialogErrors: {}
    };
  },
  computed: {
    featureName() {
      return this.$route.params.feature;
    },
    featuresData() {
      return this.$store.getters['admin/getPrompts'] || [];
    },
    currentFeature() {
      if (!this.featuresData || this.featuresData.length === 0) return null;
      return this.featuresData.find(f => f.feature === this.featureName);
    },
    featureDisplayName() {
      return this.currentFeature ? this.currentFeature.name : this.featureName;
    },
    filteredPrompts() {
        if (!this.currentFeature || !this.currentFeature.prompts) return [];
        if (!this.searchList) return this.currentFeature.prompts;
        
        const search = this.searchList.toLowerCase();
        return this.currentFeature.prompts.filter(p => 
            (p.type && p.type.toLowerCase().includes(search)) ||
            (p.content && p.content.toLowerCase().includes(search))
        );
    }
  },
  mounted(){
    if (!this.featuresData || this.featuresData.length === 0) {
      this.$store.dispatch('admin/fetchPrompts', this.axios);
    }
  },
  methods: {
    back() {
      this.$router.back();
    },
    // Dialog Control
    openAddDialog() {
      this.dialogMode = PROMPT_MODES.ADD;
      this.dialogPromptData = {
        type: '',
        feature: this.featureName,
        name: this.featureDisplayName,
        status: true,
        content: ''
      };
      this.dialogErrors = {};
      this.activeDialog = true;
    },
    editPrompt(prompt) {
      this.dialogMode = PROMPT_MODES.EDIT;
      this.dialogPromptData = { ...prompt };
      this.dialogErrors = {};
      this.activeDialog = true;
    },
    handleDialogSave(promptData) {
      if (this.dialogMode === PROMPT_MODES.ADD) {
        this.savePrompt(promptData);
      } else {
        this.promptUpdate(promptData);
      }
    },
    // Add Prompt
    async savePrompt(promptData) {
      this.dialogPromptData = promptData || this.dialogPromptData;
      this.dialogErrors = {};
      let hasErrors = false;

      if (!this.dialogPromptData.content || this.dialogPromptData.content.trim() === '') {
        this.dialogErrors.content = [MESSAGES.ERROR.CONTENT_REQUIRED];
        hasErrors = true;
      }
      if (!this.dialogPromptData.type) {
        this.dialogErrors.type = [MESSAGES.ERROR.TYPE_REQUIRED];
        hasErrors = true;
      }
      if (hasErrors) {
        this.$store.commit('sbError', MESSAGES.ERROR.FILL_REQUIRED_FIELDS);
        return;
      }

      try {
        await this.$store.dispatch('admin/createFeaturePrompt', {
          axiosInstance: this.axios,
          featureData: this.dialogPromptData
        });
        this.$store.commit('sbSuccess', MESSAGES.SUCCESS.FEATURE_PROMPT_CREATED);
        this.activeDialog = false;
        this.$store.dispatch('admin/fetchPrompts', this.axios);
      } catch (error) {
        console.error('[PromptsDetails]: savePrompt error', error);
        this.$store.commit('sbError', error.response?.data?.message || error.message || MESSAGES.ERROR.UNKNOWN_ERROR);
      }
    },
    // Edit Prompt
    async promptUpdate(promptData) {
      this.dialogPromptData = promptData || this.dialogPromptData;
      try {
        await this.$store.dispatch('admin/updatePromptById', {
          axiosInstance: this.axios,
          promptData: this.dialogPromptData
        });
        this.$store.commit('sbSuccess', MESSAGES.SUCCESS.PROMPT_UPDATED);
        this.activeDialog = false;
        this.dialogPromptData = {};
        this.$store.dispatch('admin/fetchPrompts', this.axios);
      } catch (error) {
        console.error('[PromptsDetails]: promptUpdate error', error);
        this.$store.commit('sbError', error.response?.data?.message || error.message || MESSAGES.ERROR.UNKNOWN_ERROR);
      }
    }
  }
};
</script>
